import { useState, useRef, useCallback } from "react";

/**
 * Custom hook for managing asset attributes and asset popup operations.
 * This hook extracts common asset management logic used across multiple components
 * like BatteryConfiguration, GensetConfiguration, Library, etc.
 */
export const useAssetManagement = ({
  callSnackbar,
  defaultAssetAttribute,
  defaultAvailableAttributeName,
  copyDefaultAttribute,
  setCopyDefaultAttribute,
  setCurrentAvailableAttributes,
  currentAvailableAttributes,
  isExtendedLibrary,
  addStatus,
  setAddStatus,
  setLoading,
  setPopupStatus,
  saveAssetCallback,
}) => {
  const inputRefs = useRef([]);
  const [asset, setAsset] = useState({ type: "", value: "" });
  const [prevAssetData, setPrevAssetData] = useState(null);
  const [updatedAsset, setUpdatedAsset] = useState({});
  const [editAssetData, setEditAssetData] = useState({});
  const [assetDetails, setAssetDetails] = useState({
    assets: [],
    assetsData: [],
    assetsType: [],
    assetAttributes: [],
  });

  const focusInput = useCallback((id) => {
    setTimeout(() => {
      if (inputRefs.current[id]) {
        inputRefs.current[id].focus();
      }
    }, 0);
  }, []);

  const onCloseAssetPopUp = useCallback(() => {
    setPopupStatus((prevState) => ({
      ...prevState,
      addAssetPopupStatus: false,
    }));
    setAsset({
      type: "",
      value: "",
    });
    setAddStatus(false);
  }, [setPopupStatus, setAddStatus]);

  const prepareAssetData = useCallback(
    (data) => {
      const preparedData = { ...data };
      preparedData.attributes = [];
      let newAttributes = [...assetDetails.assetAttributes].map(
        ({ isEdit, ...rest }) => rest
      );
      newAttributes.forEach((attribData, index) => {
        newAttributes[index].id = 0;
        newAttributes[index].assetId = 0;
      });
      preparedData.isExtendedLibrary = isExtendedLibrary;
      preparedData.status = "A";
      preparedData.assetName = "";
      preparedData.attributes = newAttributes;
      return preparedData;
    },
    [assetDetails.assetAttributes, isExtendedLibrary]
  );

  const onUpdateAsset = useCallback(
    (data) => {
      setLoading(true);
      const preparedData = prepareAssetData(data);
      saveAssetCallback(preparedData);
    },
    [setLoading, prepareAssetData, saveAssetCallback]
  );

  const resetAssetValues = useCallback(() => {
    setAssetDetails((prevState) => ({
      ...prevState,
      assetAttributes: [],
    }));
    setAddStatus(false);
    setLoading(false);
    setPopupStatus((prevState) => ({
      ...prevState,
      addAssetPopupStatus: false,
    }));
  }, [setAddStatus, setLoading, setPopupStatus]);

  const getAssetDetailsAttribute = useCallback(
    (assetType) => {
      const assetId = Number(assetType);
      const assetData = [...(defaultAssetAttribute || [])];
      const selectedAssetData = assetData
        ?.filter((data) => data.id === assetId)?.[0]
        ?.defaultAttributes?.map((attr, index) => ({
          type: attr.attributeName,
          value: attr.attributeValue,
          id: index,
          isEdit: false,
        }));
      const typesToRemove = selectedAssetData?.map((item) => item.type);
      const filteredArray = (defaultAvailableAttributeName || [])?.filter(
        (item) => !typesToRemove?.includes(item)
      );
      setCurrentAvailableAttributes(filteredArray);
      setCopyDefaultAttribute?.(selectedAssetData);
      if (addStatus) {
        setAssetDetails((prevState) => ({
          ...prevState,
          assetAttributes: selectedAssetData || [],
        }));
      }
    },
    [
      defaultAssetAttribute,
      defaultAvailableAttributeName,
      addStatus,
      setCurrentAvailableAttributes,
      setCopyDefaultAttribute,
    ]
  );

  const handleAssetAttribute = useCallback(
    (e, assetAttribute) => {
      setPrevAssetData(assetAttribute);
      const { name, value } = e.target;
      if (/^(\d+(\.\d*)?|)$/.test(value)) {
        setAssetDetails((prevState) => {
          const updateAsset = prevState.assetAttributes.map((asset) => {
            if (asset.id === assetAttribute.id) {
              return { ...asset, [name]: value };
            }
            return { ...asset };
          });
          setUpdatedAsset({ attributes: updateAsset });
          return {
            ...prevState,
            assetAttributes: updateAsset,
          };
        });
      }
    },
    []
  );

  const handleAsset = useCallback((e) => {
    const { name, value } = e.target;
    const checkValue = value === "" || /^\d+(\.\d*)?$/.test(value);
    if (checkValue) {
      setAsset((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleAssetSelection = useCallback((e) => {
    const { name, value } = e.target;
    setAsset((prev) => ({ ...prev, [name]: value }));
  }, []);

  const addAttributeToLibrary = useCallback(
    (index = 0) => {
      let newAttributeData = { id: index, ...asset };
      const uniqueName = assetDetails?.assetAttributes
        .map((item) => item.type)
        .includes(newAttributeData.type);
      const filteredSecondArray = (currentAvailableAttributes || defaultAvailableAttributeName)?.filter(
        (item) => item !== newAttributeData.type
      );
      setCurrentAvailableAttributes(filteredSecondArray);
      if (newAttributeData.type !== "" && newAttributeData.value !== "") {
        if (!uniqueName) {
          setAssetDetails((prevState) => {
            if (prevState?.assetAttributes) {
              return {
                ...prevState,
                assetAttributes: [
                  ...prevState.assetAttributes,
                  newAttributeData,
                ],
              };
            } else {
              return {
                ...prevState,
                assetAttributes: [newAttributeData],
              };
            }
          });
          setAsset({ type: "", value: "" });
        } else {
          callSnackbar("error", "Asset attributes types are unique");
        }
      } else {
        callSnackbar("error", "Asset attributes type and value's are mandatory");
      }
    },
    [
      asset,
      assetDetails?.assetAttributes,
      currentAvailableAttributes,
      defaultAvailableAttributeName,
      callSnackbar,
      setCurrentAvailableAttributes,
    ]
  );

  const handleEditAsset = useCallback(
    (data) => {
      setEditAssetData(data);
      setAssetDetails((prevState) => {
        const enableAsset = prevState.assetAttributes.map((assetItem) => {
          if (assetItem.id === data.id) {
            focusInput(assetItem.id);
            return { ...assetItem, isEdit: true };
          } else {
            return { ...assetItem, isEdit: false };
          }
        });
        return {
          ...prevState,
          assetAttributes: enableAsset,
        };
      });
    },
    [focusInput]
  );

  const handleCancelUpdateAsset = useCallback(
    (assetData) => {
      setAssetDetails((prevState) => {
        const updatedAssetList = prevState.assetAttributes.map((assetItem) => {
          if (assetItem.id === assetData.id) {
            return { ...editAssetData, isEdit: false };
          }
          return { ...assetItem, isEdit: false };
        });
        return {
          ...prevState,
          assetAttributes: updatedAssetList,
        };
      });
      setPrevAssetData(null);
    },
    [editAssetData]
  );

  const handleOkEditAsset = useCallback(() => {
    setAssetDetails((prevState) => {
      const enableAsset = prevState.assetAttributes.map((assetItem) => {
        if (assetItem.value === "") {
          callSnackbar("error", "Asset attributes type and value's are mandatory");
          return { ...assetItem };
        } else {
          return { ...assetItem, isEdit: false };
        }
      });
      return {
        ...prevState,
        assetAttributes: enableAsset,
      };
    });
  }, [callSnackbar]);

  const handleDeleteAsset = useCallback((id) => {
    setAssetDetails((prevState) => {
      const updateAsset = prevState.assetAttributes.filter(
        (assetItem) => assetItem.id !== id
      );
      return {
        ...prevState,
        assetAttributes: updateAsset,
      };
    });
  }, []);

  const addUpdateDataToAssetList = useCallback((data, status) => {
    if (status) {
      setAssetDetails((prevState) => {
        const newAssetList = [...prevState.assetsData, data];
        return {
          ...prevState,
          assets: newAssetList,
          assetsData: newAssetList,
        };
      });
    } else {
      setAssetDetails((prevState) => {
        const newAssetList = [...prevState.assetsData];
        const assetIndex = newAssetList.findIndex(
          (assetData) => assetData.assetId === data.assetId
        );
        if (assetIndex !== -1) {
          newAssetList[assetIndex] = data;
        }
        return {
          ...prevState,
          assets: newAssetList,
          assetsData: newAssetList,
        };
      });
    }
  }, []);

  const resetAssetList = useCallback(() => {
    setAssetDetails((prevState) => ({
      ...prevState,
      assets: prevState.assetsData,
    }));
  }, []);

  return {
    // Refs
    inputRefs,
    // State
    asset,
    setAsset,
    prevAssetData,
    setPrevAssetData,
    updatedAsset,
    setUpdatedAsset,
    editAssetData,
    setEditAssetData,
    assetDetails,
    setAssetDetails,
    // Functions
    focusInput,
    onCloseAssetPopUp,
    prepareAssetData,
    onUpdateAsset,
    resetAssetValues,
    getAssetDetailsAttribute,
    handleAssetAttribute,
    handleAsset,
    handleAssetSelection,
    addAttributeToLibrary,
    handleEditAsset,
    handleCancelUpdateAsset,
    handleOkEditAsset,
    handleDeleteAsset,
    addUpdateDataToAssetList,
    resetAssetList,
  };
};

export default useAssetManagement;
